/**
 * Engine Capability Helpers
 *
 * Utility functions for querying engine capabilities and checking query safety.
 * This file is process-agnostic (no React/Electron imports).
 */

import { ENGINE_REGISTRY } from './engineDefinitions';
import type {
  DatabaseEngineId,
  EngineDefinition,
  QueryExecutionCapabilities,
  LanguageServiceCapabilities,
  ScriptingCapabilities,
  ConnectionCapabilities,
  TableExplorerCapabilities,
  SecurityCapabilities,
  SqlIntellisenseEngine,
  WorkspaceFeatureCapabilities,
  SpatialCapabilities,
  CapabilityCategories,
  ParityGap,
  ParityGapFeature,
  DangerousQueryPatterns,
} from './engineTypes';

/**
 * Get the complete engine definition for an engine ID.
 * Returns undefined if the engine is not registered.
 */
export const getEngine = (id: DatabaseEngineId): EngineDefinition | undefined => {
  return ENGINE_REGISTRY[id];
};

/**
 * Get the engine definition, throwing if not found.
 */
export const getEngineOrThrow = (id: DatabaseEngineId): EngineDefinition => {
  const engine = ENGINE_REGISTRY[id];
  if (!engine) {
    throw new Error(`Unknown database engine: ${id}`);
  }
  return engine;
};

/**
 * Check if an engine ID is valid.
 */
export const isValidEngineId = (id: string): id is DatabaseEngineId => {
  return id in ENGINE_REGISTRY;
};

// Type-safe capability category names
type CapabilityCategory = keyof CapabilityCategories;

// Maps category to its capability keys
type CapabilityMap = {
  queryExecution: keyof QueryExecutionCapabilities;
  languageServices: keyof LanguageServiceCapabilities;
  scripting: keyof ScriptingCapabilities;
  connection: keyof ConnectionCapabilities;
  tableExplorer: keyof TableExplorerCapabilities;
  security: keyof SecurityCapabilities;
  intellisense: keyof SqlIntellisenseEngine;
  workspaceFeatures: keyof WorkspaceFeatureCapabilities;
  spatial: keyof SpatialCapabilities;
};

/**
 * Type-safe capability check.
 * Returns true if the engine supports the specified capability, false otherwise.
 *
 * @param id Engine ID
 * @param category Capability category (queryExecution, languageServices, scripting, connection, tableExplorer)
 * @param capability Specific capability within the category
 * @returns Boolean indicating whether the capability is supported
 *
 * @example
 * ```ts
 * if (engineSupports('pgsql', 'queryExecution', 'supportsMultipleResultSets')) {
 *   // Show multi-resultset tabs
 * }
 * ```
 */
export const engineSupports = <C extends CapabilityCategory>(
  id: DatabaseEngineId,
  category: C,
  capability: CapabilityMap[C]
): boolean => {
  const engine = ENGINE_REGISTRY[id];
  if (!engine) return false;

  const categoryCapabilities = engine.capabilities[category] as Record<string, unknown>;
  const value = categoryCapabilities[capability as string];
  return typeof value === 'boolean' ? value : false;
};

/**
 * Get all capabilities for a specific category.
 */
export const getCapabilities = <C extends CapabilityCategory>(
  id: DatabaseEngineId,
  category: C
): CapabilityCategories[C] | undefined => {
  const engine = ENGINE_REGISTRY[id];
  return engine?.capabilities[category];
};

/**
 * Get all parity gaps for an engine.
 */
export const getParityGaps = (id: DatabaseEngineId): readonly ParityGap[] => {
  return ENGINE_REGISTRY[id]?.parityGaps ?? [];
};

/**
 * Check if an engine has a specific parity gap.
 */
export const hasParityGap = (id: DatabaseEngineId, feature: ParityGapFeature): boolean => {
  return getParityGaps(id).some((gap) => gap.feature === feature);
};

/**
 * Get parity gaps by severity.
 */
export const getParityGapsBySeverity = (
  id: DatabaseEngineId,
  severity: ParityGap['severity']
): readonly ParityGap[] => {
  return getParityGaps(id).filter((gap) => gap.severity === severity);
};

/**
 * Get dangerous query patterns for an engine.
 */
export const getDangerousPatterns = (id: DatabaseEngineId): DangerousQueryPatterns | undefined => {
  return ENGINE_REGISTRY[id]?.dangerousQueryPatterns;
};

/**
 * Get display info for an engine (name, icon, etc.).
 */
export const getEngineDisplayInfo = (id: DatabaseEngineId) => {
  return ENGINE_REGISTRY[id]?.display;
};

/**
 * Get the default port for an engine.
 */
export const getDefaultPort = (id: DatabaseEngineId): number => {
  return ENGINE_REGISTRY[id]?.display.defaultPort ?? 0;
};

/**
 * Check if an error message indicates a lost connection for the specified engine.
 *
 * @param id Engine ID
 * @param errorMessage Error message to check
 * @returns Boolean indicating whether the error indicates a lost connection
 *
 * @example
 * ```ts
 * if (isConnectionLostError('mssql', errorText)) {
 *   markConnectionAsDisconnected();
 * }
 * ```
 */
export const isConnectionLostError = (
  id: DatabaseEngineId,
  errorMessage: string
): boolean => {
  const engine = ENGINE_REGISTRY[id];
  if (!engine?.connectionErrorPatterns) return false;

  return engine.connectionErrorPatterns.connectionLost.some((pattern) =>
    pattern.test(errorMessage)
  );
};

/**
 * Compare capabilities between two engines.
 * Returns a list of capabilities that differ.
 */
export const compareEngineCapabilities = <C extends CapabilityCategory>(
  engine1: DatabaseEngineId,
  engine2: DatabaseEngineId,
  category: C
): Array<{
  capability: string;
  engine1Value: unknown;
  engine2Value: unknown;
}> => {
  const caps1 = getCapabilities(engine1, category);
  const caps2 = getCapabilities(engine2, category);

  if (!caps1 || !caps2) return [];

  const differences: Array<{
    capability: string;
    engine1Value: unknown;
    engine2Value: unknown;
  }> = [];

  const keys = Object.keys(caps1) as Array<keyof typeof caps1>;
  for (const key of keys) {
    if (caps1[key] !== caps2[key]) {
      differences.push({
        capability: key as string,
        engine1Value: caps1[key],
        engine2Value: caps2[key],
      });
    }
  }

  return differences;
};
